import React, { useEffect, useRef, useState } from 'react';
import {
  ListProps,
  TextField,
  RowClickFunction,
  Identifier,
  RaRecord,
  FunctionField,
} from 'react-admin';

import {
  TextInput,
  ReferenceInput,
  SelectInput,
  Grid,
  GridList,
  Forbidden,
  SelectArrayInput,
} from '@components';
import { useExporter, useRbacEdit, useRbacList } from '@hooks';
import { cropHttp, VarietyOption } from '@network';
import { Crop } from '@types';
import { CropCreator } from '@components/Crops/CropCreator';
import { useSettingsContext } from '@providers/settings-provider';
import { CropListActions } from '@pages/crop/CropListActions';

export const CropList = (props: ListProps) => {
  const [varietyOptions, setVarietyOptions] = useState<VarietyOption[]>([]);

  const filters = [
    <TextInput source="q" key="q" variant="outlined" label="Search" alwaysOn />,
    <ReferenceInput
      reference="crop-category"
      source="categoryId"
      key="category"
      perPage={1000}
      sort={{ field: 'nameMx', order: 'ASC' }}
      alwaysOn
    >
      <SelectInput optionText="nameMx" />
    </ReferenceInput>,

    <SelectArrayInput
      key="varieties"
      source="varieties"
      label="Varieties"
      alwaysOn
      className="MS-field"
      style={{ width: 280 }}
      choices={varietyOptions.map(v => ({ id: v.value, name: v.label }))}
    />,
  ];

  const rbacList = useRbacList();
  const rbacEdit = useRbacEdit();
  const exporter = useExporter({
    filename: 'Verqor Crops',
    props: {
      'id': {
        label: 'ID',
      },
      'nameMx': {
        label: 'Name Mx',
      },
      'nameEn': {
        label: 'Name En',
      },
      'category.nameMx': {
        label: 'Category',
      },
      'type': {
        label: 'Type',
      },
      'yiel': {
        label: 'Yiel (Ton)',
      },
      'averageCost': {
        label: 'Average Cost Per Ha',
      },
      'averagePrice': {
        label: 'Average Price',
      },
      'varieties': {
        label: 'Varieties',
      },
    },
  });
  const {
    isCropCreatorActive, setIsCropCreatorActive, setIsToolbarShown,
  } = useSettingsContext();

  const [selectedCrop, setSelectedCategory] = useState<Crop | null>(null);

  const modalType = useRef<'create' | 'edit'>('create');

  const rowClick: RowClickFunction = (id: Identifier, resource: string, record: RaRecord) => {
    modalType.current = 'edit';
    setSelectedCategory(record as Crop);
    setIsCropCreatorActive(true);
    setIsToolbarShown(false);

    return false;
  };

  const openCropCreator = () => {
    modalType.current = 'create';
    setSelectedCategory(null);
    setIsCropCreatorActive(true);
    setIsToolbarShown(false);
  };

  const closeCropCategoryCreator = () => {
    modalType.current = 'create';
    setIsCropCreatorActive(false);
    setIsToolbarShown(true);
  };

  useEffect(() => {
    (async () => {
      try {
        const nextVarietyOptions = await cropHttp.getVarietyOptions();
        setVarietyOptions(nextVarietyOptions);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <>
      <GridList {...props}
        filters={filters}
        sort={{ field: 'categoryId', order: 'ASC' }}
        customCreate={openCropCreator}
        editComponent={rbacEdit ? <CropListActions /> : undefined}
        exporter={exporter}
      >
        <Grid
          rowClick={rowClick}
          defaultColumns={'id categoryId nameMx nameEn varieties'.split(' ')}
        >
          <TextField source="id" label="ID" />
          <TextField source="category.nameMx" label="Category"/>
          <TextField source="nameMx" label="Name MX" />
          <TextField source="nameEn" label="Name EN" />
          <TextField source="type" label="Type" />
          <TextField source="yiel" label="Yiel (Ton)" />
          <TextField source="averageCost" label="Average Cost Per Ha" />
          <TextField source="averagePrice" label="Average Price" />
          <FunctionField
            source="varieties"
            label="Varieties"
            render={(record: any) => {
              if (!record.varieties || record.varieties.length === 0) return '-';
              return record.varieties
                .map((v: any) => typeof v === 'string' ? v : v.variety)
                .join(', ');
            }}
          />
        </Grid>
      </GridList>

      {isCropCreatorActive && (
        <CropCreator
          type={modalType.current}
          close={closeCropCategoryCreator}
          values={selectedCrop}
        />
      )}
    </>
  );
};
